import { Link } from "react-router-dom";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STUDIO_IMG = "https://customer-assets.emergentagent.com/job_creative-boutique-4/artifacts/b73xrm92_SD%20avatar%20Seminal.jpg";

export default function ClosedPage() {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setPending(true);
    try {
      await api.post("/newsletter", { email });
      setSubscribed(true);
      toast.success("Thank you. We'll be in touch when the studio reopens.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="closed-page">
      <header className="px-6 md:px-12 lg:px-20 py-8">
        <p className="font-serif text-2xl tracking-tight leading-none">
          Susan Denny
          <span className="hidden md:inline text-muted-foreground text-sm ml-2 align-middle">— Fine Art</span>
        </p>
      </header>

      <main className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center px-6 md:px-12 lg:px-20 py-16 md:py-12">
        <div className="md:col-span-6 fade-up">
          <p className="eyebrow mb-6">A note from the studio</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight mb-10">
            The gallery is<br />
            <em className="italic font-light">closed for the season.</em>
          </h1>

          <div className="space-y-5 text-base leading-relaxed text-foreground/85 max-w-md">
            <p>
              Susan is refreshing and rearranging some of her displays for Spring.
            </p>
            <p>
              The gallery will reopen in <span className="font-medium text-foreground">September 2026</span> with new prints and a refreshed view of the studio.
            </p>
            <p className="text-muted-foreground">
              Thank you for your patience, and for following the work.
            </p>
          </div>

          <div className="mt-12 max-w-md">
            {subscribed ? (
              <p className="eyebrow text-foreground" data-testid="closed-newsletter-success">
                You're on the list. We'll be in touch when the studio reopens.
              </p>
            ) : (
              <form onSubmit={subscribe} className="flex border-b border-foreground" data-testid="closed-newsletter-form">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="closed-newsletter-email"
                  className="flex-1 bg-transparent py-3 outline-none placeholder:text-muted-foreground text-sm"
                />
                <button
                  type="submit"
                  disabled={pending}
                  data-testid="closed-newsletter-submit"
                  className="eyebrow text-foreground hover:opacity-60 transition-opacity disabled:opacity-40"
                >
                  {pending ? "Sending" : "Notify me →"}
                </button>
              </form>
            )}
            <p className="eyebrow mt-3 text-muted-foreground">
              Optional · Be the first to hear when the studio reopens.
            </p>
          </div>
        </div>

        <div className="md:col-span-5 md:col-start-8 fade-up">
          <div className="img-zoom aspect-[4/5]">
            <img
              src={STUDIO_IMG}
              alt="Susan painting en plein air"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="eyebrow text-muted-foreground mt-4">En plein air, Tasmania</p>
        </div>
      </main>

      <footer className="px-6 md:px-12 lg:px-20 py-8 border-t border-border">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Susan Denny. All works reproduced with permission of the artist.</span>
          <Link to="/admin/login" className="hair-link" data-testid="closed-admin-link">
            Studio login
          </Link>
        </div>
      </footer>
    </div>
  );
}
